/**
 * WorksheetSummary.jsx - the workbook's "3. Summary" and "4. Quote Data":
 * hours by task (NT / OT / DT) and what they sell for, the billable expense
 * days and dollars, then - 'cost-rates' only - the labour cost, the
 * non-billable expenses and the four AS SOLD / AS SOLD COST / margin blocks;
 * and the proposal's view: each task Fixed Fee or T&E, the expense budget
 * (airfare per trip, every other line per day) and the TOTAL BUDGET.
 */
import { GREEN, MUTED, Section, fmtMoney, fmtNum, td, th } from '../quotes/quoteUi';

const num = (v, strong) => (
  <td style={{ ...td, textAlign: 'right', fontSize: 11.5, fontWeight: strong ? 800 : 500, fontVariantNumeric: 'tabular-nums' }}>{v}</td>
);
const pct = (v) => `${((Number(v) || 0) * 100).toFixed(2)}%`;
const AS_SOLD = [
  ['labor', 'Labour only'],
  ['laborBillable', 'Labour + billable expenses'],
  ['laborNonBillable', 'Labour + non-billable expenses'],
  ['all', 'Labour + billable and non-billable'],
];

export default function WorksheetSummary({ calc, seesCosts, fixedFee, onFixedFee, readOnly }) {
  if (!calc) return null;
  const T = calc.totals;
  const rows = calc.tasks.filter((t) => t.hours > 0 || t.sell > 0 || t.bDays > 0 || t.nbDays > 0);
  const Q = calc.quoteData;
  const isFixed = (task) => (fixedFee || []).some((x) => String(x).toLowerCase() === String(task).toLowerCase());
  const toggle = (task) =>
    onFixedFee &&
    onFixedFee(
      isFixed(task) ? fixedFee.filter((x) => String(x).toLowerCase() !== String(task).toLowerCase()) : [...(fixedFee || []), task]
    );
  return (
    <>
      <Section
        title="3  Summary"
        hint="Hours by task as the customer is billed (NT / OT / DT), the labour sold, and the expense days. Billable expenses are passed through at cost."
      >
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="Summary by task" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[
                  'Task',
                  'NT',
                  'OT',
                  'DT',
                  'Labour sold',
                  ...(seesCosts ? ['Labour cost'] : []),
                  'Billable days',
                  'Billable exp.',
                  ...(seesCosts ? ['Non-bill. days', 'Non-bill. exp.'] : []),
                ].map((x) => (
                  <th key={x} style={th}>
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.task}>
                  <td style={{ ...td, fontSize: 12, fontWeight: 600 }}>{t.task}</td>
                  {num(fmtNum(t.nt))}
                  {num(fmtNum(t.ot))}
                  {num(fmtNum(t.dt))}
                  {num(fmtMoney(t.sell))}
                  {seesCosts && num(fmtMoney(t.cost))}
                  {num(fmtNum(t.bDays))}
                  {num(fmtMoney(t.bTotal))}
                  {seesCosts && num(fmtNum(t.nbDays))}
                  {seesCosts && num(fmtMoney(t.nbTotal))}
                </tr>
              ))}
              <tr style={{ background: '#F6F8F3' }}>
                <td style={{ ...td, fontSize: 12, fontWeight: 800 }}>TOTALS</td>
                {num(fmtNum(T.nt), 1)}
                {num(fmtNum(T.ot), 1)}
                {num(fmtNum(T.dt), 1)}
                {num(fmtMoney(T.laborSell), 1)}
                {seesCosts && num(fmtMoney(T.laborCost), 1)}
                {num(fmtNum(T.bDays), 1)}
                {num(fmtMoney(T.billableExp), 1)}
                {seesCosts && num(fmtNum(T.nbDays), 1)}
                {seesCosts && num(fmtMoney(T.nonBillableExp), 1)}
              </tr>
            </tbody>
          </table>
        </div>
        {seesCosts && calc.asSold && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8, marginTop: 12 }}>
            {AS_SOLD.map(([k, label]) => {
              const b = calc.asSold[k];
              return (
                <div key={k} style={{ border: '1px solid #E1BEE7', background: '#FBF7FC', borderRadius: 8, padding: '8px 10px' }}>
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      color: '#6A1B9A',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      marginBottom: 4,
                    }}
                  >
                    {label}
                  </div>
                  <div style={{ fontSize: 11.5 }}>
                    As sold <b style={{ float: 'right' }}>{fmtMoney(b.sold)}</b>
                  </div>
                  <div style={{ fontSize: 11.5 }}>
                    As sold cost <b style={{ float: 'right' }}>{fmtMoney(b.cost)}</b>
                  </div>
                  <div style={{ fontSize: 11.5, color: b.margin >= 0.3 ? GREEN : b.margin >= 0.15 ? '#B07503' : '#B71C1C' }}>
                    Margin <b style={{ float: 'right' }}>{pct(b.margin)}</b>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section
        title="4  Quote data"
        hint="What the proposal prints: each task Fixed Fee (a price) or T&E (hours and a price), the expense budget, and the total budget. Click a task's bill type to switch it."
      >
        <table aria-label="Quote data" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
          <thead>
            <tr>
              {['Task', 'Bill type', 'NT', 'OT', 'DT', 'Total'].map((x) => (
                <th key={x} style={th}>
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Q.tasks.map((t) => (
              <tr key={t.task}>
                <td style={{ ...td, fontSize: 12, fontWeight: 600 }}>{t.task}</td>
                <td style={td}>
                  <button
                    type="button"
                    disabled={readOnly || !onFixedFee}
                    onClick={() => toggle(t.task)}
                    aria-label={`${t.task} bill type`}
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 10,
                      border: '1px solid #D6DCCB',
                      cursor: readOnly ? 'default' : 'pointer',
                      background: t.billType === 'Fixed Fee' ? '#E3F2FD' : '#F1F8E9',
                    }}
                  >
                    {t.billType}
                  </button>
                </td>
                {num(t.nt === null ? '' : fmtNum(t.nt))}
                {num(t.ot === null ? '' : fmtNum(t.ot))}
                {num(t.dt === null ? '' : fmtNum(t.dt))}
                {num(fmtMoney(t.total))}
              </tr>
            ))}
            <tr style={{ background: '#F6F8F3' }}>
              <td style={{ ...td, fontWeight: 800, fontSize: 12 }} colSpan={5}>
                Labour total
              </td>
              {num(fmtMoney(Q.laborTotal), 1)}
            </tr>
          </tbody>
        </table>
        <table aria-label="Expense budget" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Expense', 'Budget', 'Man-days / trips', 'Total'].map((x) => (
                <th key={x} style={th}>
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Q.expenses.map((e) => (
              <tr key={e.key} style={{ opacity: e.amount > 0 ? 1 : 0.55 }}>
                <td style={{ ...td, fontSize: 12 }}>{e.label}</td>
                {num(fmtMoney(e.unit))}
                {num(fmtNum(e.qty))}
                {num(fmtMoney(e.amount))}
              </tr>
            ))}
            <tr style={{ background: '#F6F8F3' }}>
              <td style={{ ...td, fontWeight: 800, fontSize: 12 }} colSpan={3}>
                Expenses total
              </td>
              {num(fmtMoney(Q.expensesTotal), 1)}
            </tr>
            {Q.prevOverages > 0 && (
              <tr>
                <td style={{ ...td, fontSize: 12 }} colSpan={3}>
                  Previous overages (FCO)
                </td>
                {num(fmtMoney(Q.prevOverages))}
              </tr>
            )}
            <tr style={{ background: '#E8F5E9' }}>
              <td style={{ ...td, fontWeight: 900, fontSize: 13, color: GREEN }} colSpan={3}>
                TOTAL BUDGET
              </td>
              {num(fmtMoney(Q.budget), 1)}
            </tr>
          </tbody>
        </table>
        {!seesCosts && (
          <div style={{ marginTop: 8, fontSize: 10.5, color: MUTED }}>
            Cost, COGS and margin are shown to people with Staff cost rates &amp; COGS access only.
          </div>
        )}
      </Section>
    </>
  );
}
